/**
 * `/api/accounts/{id}/legion-progress` — see `LegionProgressService` for the roster-floor rule
 * (`effective = max(stored, derived)`, computed server-side on every GET). Mirrors
 * `lib/linkProgress.ts` exactly.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './api';
import { pushToast } from './events';
import type { LegionGradeDto } from './legion';

export function legionProgressQueryKey(accountId: number) {
  return ['legion-progress', accountId] as const;
}

export function useLegionProgress(accountId: number | null) {
  return useQuery({
    queryKey: legionProgressQueryKey(accountId ?? -1),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<LegionGradeDto[]>(`/api/accounts/${accountId}/legion-progress`, signal),
    enabled: accountId !== null,
  });
}

export function useUpsertLegionProgress(accountId: number) {
  const queryClient = useQueryClient();
  const key = legionProgressQueryKey(accountId);
  return useMutation({
    mutationFn: (vars: { classSlug: string; grade: string }) =>
      apiSend<LegionGradeDto>('PUT', `/api/accounts/${accountId}/legion-progress/${vars.classSlug}`, {
        grade: vars.grade,
      }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<LegionGradeDto[]>(key);
      queryClient.setQueryData<LegionGradeDto[]>(key, (old) =>
        old?.map((p) =>
          p.classSlug === vars.classSlug
            ? {
                ...p,
                storedGrade: vars.grade,
                // Optimistic effective: the real rank-max, using what we already know locally —
                // corrected by the server response/refetch either way via onSettled.
                effectiveGrade: rankOf(vars.grade) >= rankOf(p.derivedGrade) ? vars.grade : p.derivedGrade,
              }
            : p,
        ),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that legion grade.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}

const GRADE_ORDER = ['NONE', 'B', 'A', 'S', 'SS', 'SSS'];
function rankOf(grade: string): number {
  const index = GRADE_ORDER.indexOf(grade);
  return index < 0 ? 0 : index;
}
